<template>
  <base-dialog :active="optionsDialogVisible" :title="t('optionsdialog.Options')" :cancel-text="t('common.Cancel')"
    :confirm-text="t('common.OK')" @close="close" @cancel="close" @confirm="saveOptions">
    <!-- Tabs -->
    <div class="border-b border-stone-200 dark:border-stone-700 mb-4">
      <ul role="tablist" class="flex gap-1 mb-0" @keydown="handleTabsKeydown">
        <li>
          <button id="tab-during" role="tab" :aria-selected="activeTab === 0" :tabindex="activeTab === 0 ? 0 : -1"
            :aria-controls="'panel-during'" @click="activeTab = 0"
            class="inline-flex items-center px-3 py-2 rounded-t-md border-b-2 select-none transition-colors"
            :class="activeTab === 0
              ? 'text-primary-600 border-primary-500 cursor-default'
              : 'border-transparent text-stone-600 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'">
            {{ t('optionsdialog.During spin') }}
          </button>
        </li>
        <li>
          <button id="tab-after" role="tab" :aria-selected="activeTab === 1" :tabindex="activeTab === 1 ? 0 : -1"
            :aria-controls="'panel-after'" @click="activeTab = 1"
            class="inline-flex items-center px-3 py-2 rounded-t-md border-b-2 select-none transition-colors"
            :class="activeTab === 1
              ? 'text-primary-600 border-primary-500 cursor-default'
              : 'border-transparent text-stone-600 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'">
            {{ t('optionsdialog.After spin') }}
          </button>
        </li>
        <li>
          <button id="tab-appearance" role="tab" :aria-selected="activeTab === 2" :tabindex="activeTab === 2 ? 0 : -1"
            :aria-controls="'panel-appearance'" @click="activeTab = 2"
            class="inline-flex items-center px-3 py-2 rounded-t-md border-b-2 select-none transition-colors"
            :class="activeTab === 2
              ? 'text-primary-600 border-primary-500 cursor-default'
              : 'border-transparent text-stone-600 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'">
            {{ t('optionsdialog.Appearance') }}
          </button>
        </li>
      </ul>
    </div>

    <!-- During Spin -->
    <div v-show="activeTab === 0" id="panel-during" role="tabpanel" :aria-labelledby="'tab-during'"
      class="p-4 space-y-6">
      <DuringPanel :wheelConfig="wheelConfig" @set-during-spin-sound="setDuringSpinSound"
        @set-during-spin-sound-volume="setDuringSpinSoundVolume" />
    </div>

    <!-- After Spin -->
    <div v-show="activeTab === 1" id="panel-after" role="tabpanel" :aria-labelledby="'tab-after'" class="p-4 space-y-6">
      <AfterPanel :wheelConfig="wheelConfig" :firstTextOnWheel="firstTextOnWheel"
        @set-after-spin-sound="setAfterSpinSound" @set-after-spin-sound-volume="setAfterSpinSoundVolume" />
    </div>

    <!-- Appearance -->
    <div v-show="activeTab === 2" id="panel-appearance" role="tabpanel" :aria-labelledby="'tab-appearance'"
      class="p-4 space-y-6">
      <AppearancePanel :wheelConfig="wheelConfig" @theme-selected="colorThemeName = $event" />
    </div>
  </base-dialog>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWheelStore } from '@/stores/useWheelStore';
import DuringPanel from '@components/options/DuringPanel.vue';
import AfterPanel from '@components/options/AfterPanel.vue';
import AppearancePanel from '@components/options/AppearancePanel.vue';
import BaseDialog from '../shared/BaseDialog.vue';
import * as Audio from '@services/audio/audio.js';
import * as Util from '@utils/Util.js';
import { onOpen as onDialogOpen } from '@/services/ui/DialogCoordinator.js';

const { t } = useI18n();
const wheelStore = useWheelStore();

const optionsDialogVisible = ref(false);
const wheelConfig = ref({});
const activeTab = ref(0);
const colorThemeName = ref('');
const browserIsIE = Util.browserIsIE(navigator.userAgent);
const firstTextOnWheel = computed(() => {
  if (wheelConfig.value.getFirstText) return wheelConfig.value.getFirstText();
});

const show = () => {
  wheelConfig.value = wheelStore.wheelConfig.clone();
  optionsDialogVisible.value = true;
  colorThemeName.value = '';
};

// Close if another dialog opens
let unsubscribeDialogOpen = null;
onMounted(() => {
  unsubscribeDialogOpen = onDialogOpen((name) => {
    if (name !== 'options') {
      optionsDialogVisible.value = false;
    }
  });
});
onBeforeUnmount(() => {
  if (typeof unsubscribeDialogOpen === 'function') {
    try { unsubscribeDialogOpen(); } catch { }
  }
});

const setDuringSpinSound = (sound) => { wheelConfig.value.duringSpinSound = sound; };
const setDuringSpinSoundVolume = (volume) => { wheelConfig.value.duringSpinSoundVolume = volume; };
const setAfterSpinSound = (sound) => { wheelConfig.value.afterSpinSound = sound; };
const setAfterSpinSoundVolume = (volume) => { wheelConfig.value.afterSpinSoundVolume = volume; };


const close = () => {
  Audio.stopMusicNow();
  Audio.stopAfterSpinSound();
  optionsDialogVisible.value = false;
};

const saveOptions = () => {
  Audio.stopMusicNow();
  Audio.stopAfterSpinSound();
  wheelStore.setWheelConfig(wheelConfig.value);
  if (colorThemeName.value) {
    Util.trackEvent('Wheel', 'SelectedTheme', colorThemeName.value);
  }
  optionsDialogVisible.value = false;
};


const handleTabsKeydown = (e) => {
  if (e.key === 'ArrowRight') {
    activeTab.value = (activeTab.value + 1) % 3;
    e.preventDefault();
  } else if (e.key === 'ArrowLeft') {
    activeTab.value = (activeTab.value + 2) % 3;
    e.preventDefault();
  }
};

defineExpose({ show, close, saveOptions });
</script>

<style scoped></style>
